import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveState {
  status: AutosaveStatus;
  savedAt: Date | null;
  cancel: () => void;
}

// Debounced autosave. `persist` is injected so the save target (localStorage now,
// backend PATCH later) can change without touching this hook or the editor.
// IMPORTANT: callers must memoize `draft` (useMemo) and `persist` (useCallback)
// so the effect only re-runs on real changes, not every render.
// `cancel()` clears any pending debounced save — call it before clearing the
// draft on an explicit save/cancel so a late timer can't rewrite a deleted draft.
export function useAutosave<T>(
  draft: T,
  persist: (draft: T) => void | Promise<void>,
  delay = 800,
): AutosaveState {
  const [state, setState] = useState<{ status: AutosaveStatus; savedAt: Date | null }>({
    status: 'idle',
    savedAt: null,
  });
  // Signature of the last value that was the autosave baseline. Initialised to the
  // mounted draft so loading a post never triggers a save; updated only AFTER a
  // successful persist. Comparing signatures (not a "first render" ref) keeps this
  // correct under React StrictMode's dev double-invocation of effects.
  const savedSig = useRef<string>(JSON.stringify(draft));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => {
    const sig = JSON.stringify(draft);
    if (sig === savedSig.current) return;
    setState((s) => ({ ...s, status: 'saving' }));
    let cancelled = false;

    const onSuccess = () => {
      if (cancelled) return;
      savedSig.current = sig; // advance the baseline only on a successful persist
      setState({ status: 'saved', savedAt: new Date() });
    };
    // Leave savedSig unchanged on failure so the next change retries this content.
    const onError = () => {
      if (cancelled) return;
      setState((s) => ({ status: 'error', savedAt: s.savedAt }));
    };

    timer.current = setTimeout(() => {
      timer.current = null;
      try {
        const result = persist(draft);
        // Await an async persist (backend); a sync persist (localStorage) settles now.
        if (result && typeof (result as Promise<void>).then === 'function') {
          (result as Promise<void>).then(onSuccess, onError);
        } else {
          onSuccess();
        }
      } catch {
        onError();
      }
    }, delay);

    return () => {
      cancelled = true;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [draft, persist, delay]);

  return { status: state.status, savedAt: state.savedAt, cancel };
}
